import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { showFullScreenLoading, tryHideFullScreenLoading } from "@/components/loading/fullScreen";
import { ElMessage } from "element-plus";
import { useRuntimeConfig } from "@/stores/runtimeConfig";
import router from "@/routers";
import type { ApiResponse } from "@/api/interface/apiResponse";
import qs from "qs";
import { GRAY_VERSION } from "@zhihaoscm/mo-config";
import { checkStatus } from "./helper/checkStatus";

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    loading?: boolean;
    cancel?: boolean;
    silentError?: boolean;
}

export interface RequestAxiosConfig extends AxiosRequestConfig {
    loading?: boolean;
    cancel?: boolean;
    silentError?: boolean;
}

const config: AxiosRequestConfig = {
    // 默认地址请求地址，可在 .env.** 文件中修改
    baseURL: import.meta.env.DEV ? "/custom" : (import.meta.env.VITE_API_URL as string),
    // 设置超时时间
    timeout: 30000,
    // 跨域时候允许携带凭证
    withCredentials: true,

    paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: "repeat" });
    }
};

export class RequestHttp {
    service: AxiosInstance;
    public constructor(config: AxiosRequestConfig) {
        // instantiation
        this.service = axios.create(config);

        /**
         * @description 请求拦截器
         * 客户端发送请求 -> [请求拦截器] -> 服务器
         * token校验(JWT) : 接受服务器返回的 token,存储到 vuex/pinia/本地储存当中
         */
        this.service.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                // const userStore = useUserStore();
                const runtimeConfig = useRuntimeConfig();
                // 当前请求不需要显示 loading，在 api 服务中通过指定的第三个参数: { loading: false } 来控制
                if (config.loading) {
                    config.loading = true;
                }
                config.loading && showFullScreenLoading();

                config.headers = config.headers || {};
                config.headers["X-platform"] = "console";
                config.headers["app-type"] = '1';
                config.headers.Version = GRAY_VERSION;

                const userContextString = localStorage.getItem("zhihao-web-user");

                if (userContextString) {
                    const userContext = JSON.parse(userContextString);
                    if (userContext.zhihaoWebToken) {
                        config.headers.Authorization = userContext.zhihaoWebToken;
                    }
                }

                // if (userStore.token) {
                //     config.headers.set("Authorization", userStore.token);
                // }
                // 只有登录短信才有模板，其他模板均使用链云短信模板，管理后台只配置了登录模板，其他短信默认均为链云模板
                if (runtimeConfig.public.tenantId && !config.data?.noNeedTenantId) {
                    config.headers.set("tenantId", runtimeConfig.public.tenantId);
                }

                return config;
            },
            (error: AxiosError) => {
                return Promise.reject(error);
            }
        );

        /**
         * @description 响应拦截器
         *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
         */
        this.service.interceptors.response.use(
            async response => {
                const data: ApiResponse<any> = response.data;
                const responseConfig = response.config as CustomAxiosRequestConfig;
                tryHideFullScreenLoading();
                // 全局错误信息拦截（防止下载文件的时候返回数据流，没有 code 直接报错）
                if (data.code && data.code !== "0") {
                    // 部分业务会主动用失败请求做兜底判断，这类请求不展示全局错误。
                    if (!responseConfig.silentError) ElMessage.error(data.message);
                    return Promise.reject(data);
                }
                // 成功请求（在页面上除非特殊情况，否则不用处理失败逻辑）
                return data as any;
            },
            async (error: AxiosError) => {
                const { response } = error;
                const requestConfig = error.config as CustomAxiosRequestConfig | undefined;
                tryHideFullScreenLoading();
                if (response?.status === 401) {
                  let url = ''
                  if(window.location.protocol === 'http:') {
                    url = 'http://localhost:5173/login'
                  } else {
                    url = window.location.origin + '/login'
                  }
                    window.open(url, "_self");
                    return;
                }
                if (!requestConfig?.silentError) {
                    // 请求超时 && 网络错误单独判断，没有 response
                    if (error.message.indexOf("timeout") !== -1) ElMessage.error("请求超时！请您稍后重试");
                    if (error.message.indexOf("Network Error") !== -1) ElMessage.error("网络错误！请您稍后重试");
                    // 根据服务器响应的错误状态码，做不同的处理
                    if (response) {
                        await checkStatus(response);
                    }
                }
                // 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
                if (!window.navigator.onLine) await router.replace("/500");
                return Promise.reject(error);
            }
        );
    }
}

export default new RequestHttp(config).service;
